import { useMutation, useQueryClient } from '@tanstack/react-query';

import { medicalStaffApi } from '../api/medical-staff.adapter';
import type {
  AddOptionInput,
  ExportMedicalStaffParams,
  InviteMedicalStaffInput,
  MedicalStaff,
  MedicalStaffOptions,
  UpdateMedicalStaffInput,
} from '../types/medical-staff.types';
import { medicalStaffQueryKeys } from './use-medical-staff';

export function useInviteMedicalStaff() {
  const qc = useQueryClient();
  return useMutation<MedicalStaff, Error, InviteMedicalStaffInput>({
    mutationFn: (input) => medicalStaffApi.inviteMedicalStaff(input),
    onSuccess: () => {
      // Refresh every list permutation. Options cache is unaffected.
      qc.invalidateQueries({ queryKey: medicalStaffQueryKeys.all });
    },
  });
}

export function useResendMedicalStaffInvitation() {
  return useMutation<void, Error, number>({
    mutationFn: (id) => medicalStaffApi.resendInvitation(id),
  });
}

export function useUpdateMedicalStaff() {
  const qc = useQueryClient();
  return useMutation<MedicalStaff, Error, { id: number; input: UpdateMedicalStaffInput }>({
    mutationFn: ({ id, input }) => medicalStaffApi.updateMedicalStaff(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: medicalStaffQueryKeys.all });
    },
  });
}

export function useWithdrawMedicalStaff() {
  const qc = useQueryClient();
  return useMutation<MedicalStaff, Error, number>({
    mutationFn: (id) => medicalStaffApi.withdrawMedicalStaff(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: medicalStaffQueryKeys.all });
    },
  });
}

// PRD 03 §3.8 (v1.3): approve a staff-initiated self-withdraw request. Server flips
// status → Withdrawn and clears withdrawRequest, so refresh every list permutation.
export function useApproveWithdrawMedicalStaff() {
  const qc = useQueryClient();
  return useMutation<MedicalStaff, Error, number>({
    mutationFn: (id) => medicalStaffApi.approveWithdrawMedicalStaff(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: medicalStaffQueryKeys.all });
    },
  });
}

// PRD 03 §3.8 (v1.3): reject a staff-initiated self-withdraw request. Server clears
// withdrawRequest only (status stays Registered) — still refresh so the chip drops.
export function useRejectWithdrawMedicalStaff() {
  const qc = useQueryClient();
  return useMutation<MedicalStaff, Error, number>({
    mutationFn: (id) => medicalStaffApi.rejectWithdrawMedicalStaff(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: medicalStaffQueryKeys.all });
    },
  });
}

// PRD 03 §3.7 (v1.2): triggers the temporary-password email send.
// No cache invalidation — the row state doesn't change (status stays Registered).
export function useResetMedicalStaffPassword() {
  return useMutation<void, Error, number>({
    mutationFn: (id) => medicalStaffApi.resetPassword(id),
  });
}

export function useAddMedicalStaffDepartment() {
  const qc = useQueryClient();
  return useMutation<MedicalStaffOptions, Error, AddOptionInput>({
    mutationFn: (input) => medicalStaffApi.addDepartmentOption(input),
    // Replace the options cache directly — the response is the full updated pool.
    onSuccess: (next) => {
      qc.setQueryData(medicalStaffQueryKeys.options(), next);
    },
  });
}

export function useAddMedicalStaffJobRole() {
  const qc = useQueryClient();
  return useMutation<MedicalStaffOptions, Error, AddOptionInput>({
    mutationFn: (input) => medicalStaffApi.addJobRoleOption(input),
    onSuccess: (next) => {
      qc.setQueryData(medicalStaffQueryKeys.options(), next);
    },
  });
}

// Not a true server-mutating action — but useMutation gives us isPending / error
// state without polluting the query cache (the response is consumed once for CSV
// generation and then discarded).
export function useExportMedicalStaff() {
  return useMutation<MedicalStaff[], Error, ExportMedicalStaffParams>({
    mutationFn: (params) => medicalStaffApi.exportMedicalStaff(params),
  });
}
